import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useContext,
} from 'react'
import { connect } from 'react-redux'
import { AgGridReact } from 'ag-grid-react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { DatePicker, Button, Modal, message } from 'antd'
import moment from 'moment'
import { UtilityUnitSelection } from '../../core'
import { Title, CriteriaRow, Content } from 'core/screen-util'
import {
  getApiTargetInfo,
  getEventTypes,
  getContribOperCondition,
  getUnits,
  getUnitNameMap,
} from '../../core/selectors'
import {
  getAeciUnitHourly,
  getAeciHourlyEvents,
  saveAeciHourlyEvents,
  saveRepeatAeciHourlyEvents,
  deleteAeciHourlyEvents,
  getAeciEventMonthlyLock,
  saveAeciEventMonthlyLock,
  saveAeciDrl,
  validateAndUpdateAeciDrl,
  updateAeciEventComment,
} from '../aeciApi'
import { getDateHourList } from 'api/pgads.js'
import { DATE_FORMAT_API } from 'enum'
import { hasAccesPermits } from 'core/utils'
import { AECI_UNITHOURLY_WRITE } from '../../core/EventActions'
import EventModal from './EventModal'
import RepeatUntilModal from './RepeatUntilModal'
import AddRemainingDeratesModal from './AddRemainingDeratesModal'
import UpdateDescriptionModal from './UpdateDescriptionModal'
import ViewSubmitModal from './ViewSubmitModal'
import EventLockModal from './EventLockModal'
import HourEventsModal from './HourEventsModal'
import UnallocatedModal from './UnallocatedModal'
import JcTmpErrorContext from 'context/JcTmpErrorContext'
import ErrorContext from 'context/ErrorContext'
import { getCauseCodeAll, getSystemConfigurationInfo } from '../../api/pgads'
import { Filler } from '../../core/screen-util'
import { recalcDerivedRows } from './drlUtils'
import { buildColumnDefs, defaultColDef } from './columnDefs'
import { sortByRowOrder, dataToRows, eventsToRows } from './rowTransforms'

const HOURS = Array.from({ length: 24 }, (_, i) => i + 1)
const LOCKED_STATUSES = new Set(['S', 'L', 'ML'])
const SAVE_FORMAT = 'MM/DD/YYYY HH:mm'

const hasTmpErrors = result => Array.isArray(result) && result.length > 0

const UNIT_STATUS_ROW = {
  metric: 'Hourly Status',
  rowType: 'metric',
  rowOrder: 0,
}
const ADD_EVENT_ROW = { metric: 'Add Event', rowType: 'addEvent', rowOrder: 8 }
const EVENT_COUNT_ROW = {
  metric: 'Event Count',
  rowType: 'metric',
  rowOrder: 7,
}
const UNALLOCATED_ROW = {
  metric: 'Unallocated',
  rowType: 'metric',
  rowOrder: 6,
}

const STATUS_LEGEND = [
  {
    status: 'U',
    description: 'Unlocked — editable',
    color: '#f5f5f5',
    editable: true,
  },
  {
    status: 'S',
    description: 'Submitted — Event submitted/created to GADS, read-only',
    color: '#d9f7be',
    editable: false,
  },
  {
    status: 'L',
    description: 'Locked — daily lock applied by System, read-only',
    color: '#ffccc7',
    editable: false,
  },
  {
    status: 'R',
    description: 'Released Lock — mapped to Unlocked by system, editable',
    color: '#fff7e6',
    editable: true,
  },
  {
    status: 'ML',
    description: 'Monthly Lock — entire month locked, no edits allowed',
    color: '#ffccc7',
    editable: false,
  },
  {
    status: 'MU',
    description: 'Monthly Unlock — monthly lock removed, edits allowed',
    color: '#f5f5f5',
    editable: true,
  },
]

const thStyle = {
  padding: '8px 12px',
  textAlign: 'left',
  borderBottom: '1px solid #e8e8e8',
  fontWeight: 600,
}
const tdStyle = { padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }

const UnitHourly = ({
  apiTargetInfo,
  eventTypes,
  contribConditions,
  units,
  unitNameMap,
  actionPermits,
  envConfig,
}) => {
  const { setTmpError } = useContext(JcTmpErrorContext)
  const { setError } = useContext(ErrorContext)

  const [causeCodeList, setCauseCodeList] = useState([])
  const causeCodeListRef = useRef([])
  useEffect(() => {
    causeCodeListRef.current = causeCodeList
  }, [causeCodeList])
  useEffect(
    _ => {
      getCauseCodeAll(apiTargetInfo).fork(setError, setCauseCodeList)
    },
    [apiTargetInfo, setError]
  )

  const hasWrite = useMemo(
    () =>
      (envConfig && envConfig.authGodMode) ||
      hasAccesPermits({
        authGodMode: envConfig && envConfig.authGodMode,
        actionPermits,
        eventActions: [AECI_UNITHOURLY_WRITE],
        matchAny: true,
      }),
    [actionPermits, envConfig]
  )

  const [selectedDate, setSelectedDate] = useState(moment())
  const [selectedUtility, setSelectedUtility] = useState(null)
  const [selectedUnit, setSelectedUnit] = useState(null)
  const [selectedUnitType, setSelectedUnitType] = useState(null)
  const [selectedUnitName, setSelectedUnitName] = useState(null)
  const [rowData, setRowData] = useState([])
  const [loading, setLoading] = useState(false)
  const [unitLockStatus, setUnitLockStatus] = useState(null)

  const [lockModalVisible, setLockModalVisible] = useState(false)
  const [lockModalLoading, setLockModalLoading] = useState(false)
  const [monthlyLockStatus, setMonthlyLockStatus] = useState(null)

  const [modalVisible, setModalVisible] = useState(false)
  const [modalInitHour, setModalInitHour] = useState(null)
  const [modalSaveError, setModalSaveError] = useState(null)
  const [updateModalVisible, setUpdateModalVisible] = useState(false)
  const [updateModalEventRow, setUpdateModalEventRow] = useState(null)
  const [viewSubmitVisible, setViewSubmitVisible] = useState(false)
  const [repeatModalVisible, setRepeatModalVisible] = useState(false)
  const [repeatModalError, setRepeatModalError] = useState(null)
  const [addDeratesVisible, setAddDeratesVisible] = useState(false)
  const [addDeratesError, setAddDeratesError] = useState(null)
  const [legendVisible, setLegendVisible] = useState(false)
  const [hourEventsModal, setHourEventsModal] = useState({
    visible: false,
    hour: null,
    eventRow: null,
  })
  const [unallocModal, setUnallocModal] = useState({
    visible: false,
    hour: null,
  })

  const [dateHourList, setDateHourList] = useState([])
  const [systemConfigurationInfo, setSystemConfigurationInfo] = useState([])

  const intervalRetrieveTimeOut = useMemo(() => {
    const entry = systemConfigurationInfo.find(
      info => info.key === 'AECI_UNIT_HOURLY_AUTO_RETRIEVE_TIMEOUT'
    )
    return parseInt((entry || { value: '5' }).value, 10)
  }, [systemConfigurationInfo])

  useEffect(() => {
    if (!selectedDate || !apiTargetInfo) return
    getDateHourList(apiTargetInfo)(selectedDate, selectedDate).fork(() => {},
    setDateHourList)
    getSystemConfigurationInfo(apiTargetInfo).fork(
      setError,
      setSystemConfigurationInfo
    )
  }, [apiTargetInfo, selectedDate, setError])

  useEffect(() => {
    if (!selectedUnit || !selectedDate || !apiTargetInfo) {
      setMonthlyLockStatus(null)
      return
    }
    getAeciEventMonthlyLock(apiTargetInfo)({
      unitId: selectedUnit,
      year: selectedDate.year(),
      month: selectedDate.month() + 1,
    }).fork(
      () => {},
      lock => setMonthlyLockStatus(lock ? lock.status : null)
    )
  }, [apiTargetInfo, selectedUnit, selectedDate])

  const { showH2A, showH3 } = useMemo(() => {
    const dateStr = selectedDate ? selectedDate.format('YYYY-MM-DD') : null
    const entry = dateStr && dateHourList.find(d => d.date === dateStr)
    const hours = (entry && entry.hours) || []
    return {
      showH2A: hours.includes(2.2),
      showH3: hours.length === 0 || hours.includes(3),
    }
  }, [selectedDate, dateHourList])

  const columnDefs = useMemo(() => buildColumnDefs(hasWrite, showH2A, showH3), [
    hasWrite,
    showH2A,
    showH3,
  ])

  const buildRows = useCallback(
    (unitData, events, lockStatus) => {
      const metricRows = dataToRows(unitData)
      return sortByRowOrder(
        recalcDerivedRows(
          [
            UNIT_STATUS_ROW,
            ...metricRows,
            UNALLOCATED_ROW,
            EVENT_COUNT_ROW,
            ...(hasWrite ? [ADD_EVENT_ROW] : []),
            ...eventsToRows(events, causeCodeListRef.current),
          ],
          lockStatus
        )
      )
    },
    [hasWrite]
  )

  const handleRetrieve = useCallback(() => {
    if (!selectedUnit) return
    setRowData([])
    setLoading(true)
    const params = {
      unitId: selectedUnit,
      date: selectedDate.format(DATE_FORMAT_API),
    }
    getAeciUnitHourly(apiTargetInfo)(params).fork(
      () => {
        message.error('Failed to retrieve unit hourly data')
        setLoading(false)
      },
      unitData => {
        getAeciHourlyEvents(apiTargetInfo)(params).fork(
          () => {
            message.error('Failed to retrieve events')
            setLoading(false)
          },
          events => {
            const synthetic = events.find(e => e.eventNum < 0)
            const lockStatus = synthetic ? synthetic.isSubmitted || null : null
            const realEvents = events.filter(e => e.eventNum >= 0)
            setUnitLockStatus(lockStatus)
            setRowData(buildRows(unitData, realEvents, lockStatus))
            setLoading(false)
          }
        )
      }
    )
  }, [apiTargetInfo, selectedUnit, selectedDate, buildRows])

  const refreshEvents = useCallback(() => {
    if (!selectedUnit) return
    const params = {
      unitId: selectedUnit,
      date: selectedDate.format(DATE_FORMAT_API),
    }
    getAeciUnitHourly(apiTargetInfo)(params).fork(
      () => {},
      unitData => {
        getAeciHourlyEvents(apiTargetInfo)(params).fork(
          () => {},
          events => {
            const synthetic = events.find(e => e.eventNum < 0)
            const lockStatus = synthetic ? synthetic.isSubmitted || null : null
            const realEvents = events.filter(e => e.eventNum >= 0)
            setUnitLockStatus(lockStatus)
            setRowData(buildRows(unitData, realEvents, lockStatus))
          }
        )
      }
    )
  }, [apiTargetInfo, selectedUnit, selectedDate, buildRows])

  const handleRetrieveRef = useRef(handleRetrieve)
  useEffect(() => {
    handleRetrieveRef.current = handleRetrieve
  }, [handleRetrieve])

  const refreshEventsRef = useRef(refreshEvents)
  useEffect(() => {
    refreshEventsRef.current = refreshEvents
  }, [refreshEvents])

  useEffect(() => {
    if (!selectedUnit || viewSubmitVisible) return
    const id = setInterval(
      () => refreshEventsRef.current(),
      intervalRetrieveTimeOut * 60 * 1000
    )
    return () => clearInterval(id)
  }, [selectedUnit, selectedDate, intervalRetrieveTimeOut, viewSubmitVisible])

  useEffect(() => {
    if (!selectedUnit || !selectedDate) {
      setRowData([])
      return
    }
    handleRetrieveRef.current()
  }, [selectedDate, selectedUnit])

  const onCellValueChanged = useCallback(
    params => {
      if (!params.data) return
      if (monthlyLockStatus === 'ML') {
        message.error('Month is locked — save not allowed')
        handleRetrieve()
        return
      }
      if (params.data.metric !== 'DRL') return

      const field = params.colDef.field
      if (!field || !field.startsWith('h') || field === 'h2a') return
      const hNum = parseInt(field.slice(1), 10)
      if (isNaN(hNum)) return

      const newDrl =
        params.newValue !== '' && params.newValue != null
          ? parseFloat(params.newValue)
          : null
      const oldDrl =
        params.oldValue !== '' && params.oldValue != null
          ? parseFloat(params.oldValue)
          : null
      const eventStart = selectedDate
        .clone()
        .hour(hNum - 1)
        .minute(0)
        .second(0)
        .format(SAVE_FORMAT)
      const utility = selectedUnit ? selectedUnit.substring(0, 3) : ''
      const unit = selectedUnit ? selectedUnit.substring(3) : ''

      const applyDrl = val =>
        setRowData(prev =>
          sortByRowOrder(
            recalcDerivedRows(
              prev.map(r =>
                r.metric === 'DRL' ? { ...r, [`h${hNum}`]: val ?? '' } : r
              ),
              unitLockStatus
            )
          )
        )

      applyDrl(newDrl)

      validateAndUpdateAeciDrl(apiTargetInfo)({
        utility,
        unit,
        eventStart,
        oldDrl: oldDrl != null ? oldDrl : undefined,
        newDrl: newDrl != null ? newDrl : undefined,
      }).fork(
        () => message.error('DRL save failed'),
        result => {
          if (result.conflict) {
            const dbVal =
              result.dbValue != null
                ? Number(result.dbValue).toFixed(1)
                : 'empty'
            const myVal = newDrl != null ? Number(newDrl).toFixed(1) : 'empty'
            Modal.confirm({
              title: 'DRL Conflict',
              content: `Another user changed this value to ${dbVal} (you had ${
                oldDrl != null ? Number(oldDrl).toFixed(1) : 'empty'
              }). Which value do you want to keep?`,
              okText: `My Value (${myVal})`,
              cancelText: `Other User's Value (${dbVal})`,
              width: 420,
              onOk: () => {
                saveAeciDrl(apiTargetInfo)([
                  { utility, unit, eventStart, drl: newDrl },
                ]).fork(
                  () => message.error('DRL save failed'),
                  () => message.success('DRL saved')
                )
              },
              onCancel: () =>
                applyDrl(
                  result.dbValue != null ? Number(result.dbValue) : null
                ),
            })
            return
          }
          if (result.errorMsg) {
            message.error(result.errorMsg)
            applyDrl(oldDrl)
            return
          }
          message.success('DRL saved')
        }
      )
    },
    [
      apiTargetInfo,
      selectedDate,
      handleRetrieve,
      monthlyLockStatus,
      selectedUnit,
      unitLockStatus,
    ]
  )

  const isReadOnly = useMemo(() => {
    const statusRow = rowData.find(r => r.metric === 'Hourly Status')
    if (!statusRow) return false
    return HOURS.some(h => LOCKED_STATUSES.has(statusRow[`h${h}`]))
  }, [rowData])

  const handleAddAtHour = useCallback(
    h => {
      const statusRow = rowData.find(r => r.metric === 'Hourly Status')
      const hourStatus = statusRow && statusRow[`h${h}`]
      if (LOCKED_STATUSES.has(hourStatus)) return
      setModalSaveError(null)
      setModalInitHour(h)
      setModalVisible(true)
    },
    [rowData]
  )

  const onCellClicked = useCallback(
    p => {
      if (!p.data) return
      if (p.data.rowType === 'addEvent') {
        const field = p.colDef && p.colDef.field
        if (!field || !field.startsWith('h') || field === 'h2a') {
          setModalInitHour(null)
          setModalVisible(true)
        }
        return
      }
      if (p.data.rowType === 'event') {
        const field = p.colDef && p.colDef.field
        if (field && field.startsWith('h') && field !== 'h2a') {
          const hNum = parseInt(field.slice(1), 10)
          if (isNaN(hNum)) return
          const statusRow = rowData.find(r => r.metric === 'Hourly Status')
          const hourStatus = statusRow && statusRow[`h${hNum}`]
          if (LOCKED_STATUSES.has(hourStatus)) return
          setHourEventsModal({ visible: true, hour: hNum, eventRow: p.data })
        }
      }
      if (p.data.rowType === 'metric' && p.data.metric === 'Unallocated') {
        const field = p.colDef && p.colDef.field
        if (field && field.startsWith('h') && field !== 'h2a') {
          const hNum = parseInt(field.slice(1), 10)
          if (!isNaN(hNum)) setUnallocModal({ visible: true, hour: hNum })
        }
      }
    },
    [rowData]
  )

  const handleModalSave = useCallback(
    values => {
      if (monthlyLockStatus === 'ML') {
        message.error('Month is locked — save not allowed')
        return
      }
      const unit = selectedUnit ? selectedUnit.substr(3) : selectedUnit
      const baseEvent = {
        utility: selectedUtility,
        unit,
        eventNum: -1,
        eventType: values.eventType,
        eventStart: values.eventStart,
        availCap: values.availCap ?? null,
        causeCode: values.causeCode || 0,
        coc: values.coc || null,
        description: values.description || null,
        isSubmitted: null,
      }
      const payload = [baseEvent]
      setModalSaveError(null)
      setLoading(true)
      saveAeciHourlyEvents(apiTargetInfo)(payload).fork(
        err => {
          setError(err)
          setLoading(false)
        },
        result => {
          if (hasTmpErrors(result)) {
            setModalSaveError(result)
            setLoading(false)
          } else {
            setModalVisible(false)
            message.success('Saved')
            handleRetrieve()
          }
        }
      )
    },
    [
      apiTargetInfo,
      selectedUtility,
      selectedUnit,
      handleRetrieve,
      monthlyLockStatus,
      setError,
    ]
  )

  const handleRepeatApprove = useCallback(
    payload => {
      setRepeatModalError(null)
      setLoading(true)
      saveRepeatAeciHourlyEvents(apiTargetInfo)(payload).fork(
        err => {
          setError(err)
          setLoading(false)
        },
        result => {
          if (hasTmpErrors(result)) {
            setRepeatModalError(result)
            setLoading(false)
          } else {
            setRepeatModalVisible(false)
            message.success(`Saved (${payload.length} events)`)
            handleRetrieve()
          }
        }
      )
    },
    [apiTargetInfo, handleRetrieve, setError]
  )

  const handleAddDeratesApprove = useCallback(
    payload => {
      setAddDeratesError(null)
      setLoading(true)
      saveRepeatAeciHourlyEvents(apiTargetInfo)(payload).fork(
        err => {
          setError(err)
          setLoading(false)
        },
        result => {
          if (hasTmpErrors(result)) {
            setAddDeratesError(result)
            setLoading(false)
          } else {
            setAddDeratesVisible(false)
            message.success(`Saved (${payload.length} events)`)
            handleRetrieve()
          }
        }
      )
    },
    [apiTargetInfo, handleRetrieve, setError]
  )

  const handleDeleteEvent = useCallback(
    eventRow => {
      if (monthlyLockStatus === 'ML') {
        message.error('Month is locked — delete not allowed')
        return
      }
      const statusRow = rowData.find(r => r.metric === 'Hourly Status')
      const unlockedEntries = Object.entries(eventRow.eventDataMap || {})
        .filter(([h]) => !LOCKED_STATUSES.has(statusRow && statusRow[`h${h}`]))
        .sort(([a], [b]) => Number(a) - Number(b))
      if (!unlockedEntries.length) {
        message.warning('All hours for this event are submitted or locked')
        return
      }
      const events = unlockedEntries.flatMap(([, arr]) => arr)
      const hourList = unlockedEntries
        .map(([h]) => `H${String(Number(h)).padStart(2, '0')}`)
        .join(', ')
      const payload = events.map(e => ({
        utility: e.utility,
        unit: e.unit,
        eventNum: e.eventNum,
        eventType: e.eventType,
        eventStart: moment(e.eventStart).format(SAVE_FORMAT),
        availCap: e.availCap ?? null,
        causeCode: e.causeCode || 0,
        coc: e.coc || null,
        description: e.description || null,
        isSubmitted: e.isSubmitted || null,
      }))
      Modal.confirm({
        title: 'Delete Event',
        content: `Delete "${eventRow.metric}" for hours: ${hourList}?`,
        okText: 'Delete',
        okType: 'danger',
        onOk: () => {
          setLoading(true)
          deleteAeciHourlyEvents(apiTargetInfo)(payload).fork(
            err => {
              setError(err)
              setLoading(false)
            },
            result => {
              setLoading(false)
              if (hasTmpErrors(result)) setTmpError(result)
              else {
                message.success('Deleted')
                handleRetrieve()
              }
            }
          )
        },
      })
    },
    [
      rowData,
      apiTargetInfo,
      handleRetrieve,
      monthlyLockStatus,
      setError,
      setTmpError,
    ]
  )

  const handleUpdateEvent = useCallback(eventRow => {
    setUpdateModalEventRow(eventRow)
    setUpdateModalVisible(true)
  }, [])

  const handleUpdateSave = useCallback(
    (values, eventRow) => {
      const allEvents = Object.values(
        (eventRow && eventRow.eventDataMap) || {}
      ).flat()
      if (!allEvents.length) return
      const e = allEvents[0]
      const payload = {
        utility: e.utility,
        unit: e.unit,
        eventType: e.eventType,
        causeCode: Number(e.causeCode) || 0,
        startDate: values.startDate.format('YYYY-MM-DD'),
        startHour: values.startHour,
        endDate: values.endDate.format('YYYY-MM-DD'),
        endHour: values.endHour,
        coc: values.coc || null,
        description: values.description || null,
        availCap: null,
      }
      setUpdateModalVisible(false)
      setLoading(true)
      updateAeciEventComment(apiTargetInfo)(payload).fork(
        err => {
          setError(err)
          setLoading(false)
        },
        result => {
          if (hasTmpErrors(result)) {
            setTmpError(result)
            setLoading(false)
          } else {
            message.success('Updated')
            handleRetrieve()
          }
        }
      )
    },
    [apiTargetInfo, handleRetrieve, setError, setTmpError]
  )

  const handleLockSave = useCallback(
    ({ status, reason }) => {
      setLockModalLoading(true)
      const payload = {
        utility: selectedUtility,
        unit: selectedUnit ? selectedUnit.substr(3) : selectedUnit,
        year: selectedDate.year(),
        month: selectedDate.month() + 1,
        status,
        reason,
      }
      saveAeciEventMonthlyLock(apiTargetInfo)(payload).fork(
        err => {
          setError(err)
          setLockModalLoading(false)
        },
        result => {
          setLockModalLoading(false)
          if (hasTmpErrors(result)) {
            setTmpError(result)
          } else {
            setMonthlyLockStatus(status)
            setLockModalVisible(false)
            message.success(status === 'ML' ? 'Month locked' : 'Month unlocked')
          }
        }
      )
    },
    [
      apiTargetInfo,
      selectedUtility,
      selectedUnit,
      selectedDate,
      setError,
      setTmpError,
    ]
  )

  const gridContext = useMemo(
    () => ({
      onAddAtHour: handleAddAtHour,
      onDeleteEvent: handleDeleteEvent,
      onUpdateEvent: handleUpdateEvent,
      isReadOnly,
      hasWrite,
      statusRow: rowData.find(r => r.metric === 'Hourly Status') || null,
    }),
    [
      handleAddAtHour,
      handleDeleteEvent,
      handleUpdateEvent,
      isReadOnly,
      hasWrite,
      rowData,
    ]
  )

  const gridRef = useRef(null)
  useEffect(() => {
    if (gridRef.current && gridRef.current.api)
      gridRef.current.api.refreshCells({ force: true })
  }, [gridContext])

  const initialEventStart = useMemo(() => {
    if (!modalInitHour || !selectedDate) return null
    return selectedDate
      .clone()
      .hour(modalInitHour - 1)
      .minute(0)
      .second(0)
  }, [modalInitHour, selectedDate])

  const existingEvents = useMemo(
    () =>
      rowData
        .filter(r => r.rowType === 'event')
        .flatMap(r => Object.values(r.eventDataMap || {}).flat()),
    [rowData]
  )

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CriteriaRow>
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Title>AECI Unit Hourly</Title>

          <span>Date:</span>
          <DatePicker
            value={selectedDate}
            onChange={setSelectedDate}
            allowClear={false}
            format="MMM/DD/YYYY"
          />

          <span>Unit:</span>
          <UtilityUnitSelection
            utility={selectedUtility}
            unit={selectedUnit}
            casStyle={{ width: 280 }}
            onUnitChange={val => {
              if (val && val.length === 2) {
                setSelectedUtility(val[0])
                setSelectedUnit(val[1])
                const unit = (units || []).find(u => u.unitId === val[1])
                setSelectedUnitType(unit ? unit.id1 : null)
                const nameKey = val[0] + val[1]
                setSelectedUnitName(
                  (unitNameMap &&
                    (unitNameMap[nameKey] || unitNameMap[val[1]])) ||
                    null
                )
              }
            }}
          />

          <Button
            type="primary"
            icon="search"
            onClick={handleRetrieve}
            disabled={!selectedUnit}
            loading={loading}
          >
            Retrieve
          </Button>
          <Button
            type="primary"
            icon="left"
            disabled={!selectedUnit}
            onClick={() =>
              setSelectedDate(
                (selectedDate || moment()).clone().subtract(1, 'day')
              )
            }
          />
          <Button
            type="primary"
            icon="right"
            disabled={!selectedUnit}
            onClick={() =>
              setSelectedDate((selectedDate || moment()).clone().add(1, 'day'))
            }
          />

          {hasWrite && (
            <Button
              icon="redo"
              onClick={() => {
                setRepeatModalError(null)
                setRepeatModalVisible(true)
              }}
              disabled={!selectedUnit}
            >
              Repeat Until
            </Button>
          )}
          {hasWrite && (
            <Button
              icon="plus-circle"
              onClick={() => {
                setAddDeratesError(null)
                setAddDeratesVisible(true)
              }}
              disabled={!selectedUnit}
            >
              Add Remaining Derates
            </Button>
          )}
          {hasWrite && (
            <Button
              icon="eye"
              onClick={() => setViewSubmitVisible(true)}
              disabled={!selectedUnit}
              type="danger"
            >
              View &amp; Approve
            </Button>
          )}
          {hasWrite && (
            <Button
              icon={monthlyLockStatus === 'ML' ? 'unlock' : 'lock'}
              disabled={!selectedUnit}
              onClick={() => setLockModalVisible(true)}
              type="danger"
            >
              Monthly {monthlyLockStatus === 'ML' ? 'Unlock' : 'Lock'}
            </Button>
          )}
          <Filler />
          <Button
            icon="info-circle"
            style={{ marginLeft: 'auto' }}
            onClick={() => setLegendVisible(true)}
          >
            Status Legend
          </Button>
        </div>
      </CriteriaRow>

      <Content style={{ flex: 1, padding: 10 }}>
        <div
          className="ag-theme-alpine"
          style={{ height: '650px', width: '100%' }}
        >
          <AgGridReact
            ref={gridRef}
            columnDefs={columnDefs}
            rowData={rowData}
            defaultColDef={defaultColDef}
            suppressMovableColumns
            context={gridContext}
            tooltipShowDelay={300}
            onCellValueChanged={onCellValueChanged}
            onCellClicked={onCellClicked}
            getRowId={p =>
              p.data.rowType === 'addEvent'
                ? '__add_event__'
                : p.data.rowType === 'event'
                ? `event-${p.data.metric}`
                : p.data.metric
            }
            getRowHeight={p =>
              p.data && p.data.rowType === 'metric' ? 30 : undefined
            }
            getRowStyle={p => {
              const BOLD = new Set(['NDC', 'Net Generation', 'Unallocated'])
              if (p.data && BOLD.has(p.data.metric)) return { fontWeight: 700 }
              return undefined
            }}
          />
        </div>
      </Content>

      <EventModal
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false)
          setModalSaveError(null)
        }}
        onSave={handleModalSave}
        saveError={modalSaveError}
        saveLoading={loading}
        editData={null}
        initialEventStart={initialEventStart}
        selectedDate={selectedDate}
        existingEvents={existingEvents}
        selectedUnit={selectedUnitName || selectedUnit}
        unitType={[selectedUnitType]}
        unitHourlyRows={rowData}
        eventTypes={eventTypes}
        contribConditions={contribConditions}
        causeCodeList={causeCodeList}
      />

      <UpdateDescriptionModal
        visible={updateModalVisible}
        onCancel={() => setUpdateModalVisible(false)}
        onSave={handleUpdateSave}
        eventRow={updateModalEventRow}
        contribConditions={contribConditions}
        selectedDate={selectedDate}
      />

      <RepeatUntilModal
        visible={repeatModalVisible}
        onCancel={() => setRepeatModalVisible(false)}
        onApprove={handleRepeatApprove}
        eventRows={rowData}
        selectedDate={selectedDate}
        selectedUtility={selectedUtility}
        selectedUnit={selectedUnit}
        unitHourlyRows={rowData}
        saveLoading={loading}
        saveError={repeatModalError}
      />

      <AddRemainingDeratesModal
        visible={addDeratesVisible}
        onCancel={() => setAddDeratesVisible(false)}
        onApprove={handleAddDeratesApprove}
        selectedDate={selectedDate}
        selectedUtility={selectedUtility}
        selectedUnit={selectedUnit}
        apiTargetInfo={apiTargetInfo}
        eventTypes={eventTypes}
        causeCodeList={causeCodeList}
        saveLoading={loading}
        saveError={addDeratesError}
      />

      <ViewSubmitModal
        visible={viewSubmitVisible}
        onCancel={() => setViewSubmitVisible(false)}
        onSubmitSuccess={handleRetrieve}
        unitId={selectedUnit}
        unitName={selectedUnitName}
        selectedDate={selectedDate}
        apiTargetInfo={apiTargetInfo}
        unitHourlyRows={rowData}
      />

      <EventLockModal
        visible={lockModalVisible}
        onCancel={() => setLockModalVisible(false)}
        onSave={handleLockSave}
        unit={selectedUnitName || selectedUnit}
        year={selectedDate ? selectedDate.year() : null}
        month={selectedDate ? selectedDate.month() + 1 : null}
        currentStatus={monthlyLockStatus}
        loading={lockModalLoading}
      />

      <HourEventsModal
        visible={hourEventsModal.visible}
        onClose={() =>
          setHourEventsModal({ visible: false, hour: null, eventRow: null })
        }
        onSaved={() => {
          setHourEventsModal({ visible: false, hour: null, eventRow: null })
          handleRetrieve()
        }}
        hour={hourEventsModal.hour}
        eventRow={hourEventsModal.eventRow}
        selectedDate={selectedDate}
        selectedUnit={selectedUnit}
        apiTargetInfo={apiTargetInfo}
        hasWrite={hasWrite}
        monthlyLockStatus={monthlyLockStatus}
        unitHourlyRows={rowData}
        eventTypes={eventTypes}
        causeCodeList={causeCodeList}
      />

      <UnallocatedModal
        visible={unallocModal.visible}
        onClose={() => setUnallocModal({ visible: false, hour: null })}
        hour={unallocModal.hour}
        rowData={rowData}
      />

      <Modal
        title="Hourly Status Legend"
        visible={legendVisible}
        onCancel={() => setLegendVisible(false)}
        footer={null}
        width={480}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#fafafa' }}>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Description</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Editable</th>
            </tr>
          </thead>
          <tbody>
            {STATUS_LEGEND.map(({ status, description, color, editable }) => (
              <tr key={status}>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '2px 10px',
                      borderRadius: 3,
                      background: color,
                      fontWeight: 'bold',
                      minWidth: 32,
                    }}
                  >
                    {status}
                  </span>
                </td>
                <td style={tdStyle}>{description}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  {editable ? (
                    <span style={{ color: '#389e0d', fontWeight: 'bold' }}>
                      Yes
                    </span>
                  ) : (
                    <span style={{ color: '#cf1322', fontWeight: 'bold' }}>
                      No
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>
    </div>
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  eventTypes: getEventTypes(state),
  contribConditions: getContribOperCondition(state),
  units: getUnits(state),
  unitNameMap: getUnitNameMap(state),
})

export default connect(mapStateToProps)(UnitHourly)
